import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Code } from '#app/components/typography/code.tsx'
import { Em } from '#app/components/typography/em.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Em',
}

const component: componentProps = {
	name: 'Em',
	shortName: 'Em',
	fileName: 'em',
	category: 'typography',
	description: ['Emphasis component.'],
	features: ['Drop-in replacement for the native <em> element.', "Opts-in to your design system's typographic styles."],
	parts: [],
	usage: `<P>
	This <Em>is</Em> emphasized.
</P>`,
	styling: `.em {
	@apply font-sans italic;
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const SpanRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<P>
				This <Em>is</Em> emphasized.
			</P>
			<P className="mt-4">
				Notice that <Code>{'<em>'}</Code> tags will go inside <Code>{'<p>'}</Code> tags and inherit whatever typographical attributes that paragraph has.
			</P>
			<H2>
				{'<i>'} vs {'<em>'}
			</H2>
			<P>
				Some developers may be confused by how multiple elements seemingly produce similar visual results. <Code>{'<em>'}</Code> and <Code>{'<i>'}</Code> are a common example, since they
				both italicize text. What's the difference? Which should you use?
			</P>
			<P className="mt-4">
				By default, the visual result is the same. However, the semantic meaning is different. The <Code>{'<em>'}</Code> element represents stress emphasis of its contents, while the{' '}
				<Code>{'<i>'}</Code> element represents text that is set off from the normal prose, such as a foreign word, fictional character thoughts, or when the text refers to the
				definition of a word instead of representing its semantic meaning. (The title of a work, such as the name of a book or movie, should use <Code>{'<cite>'}</Code>.)
			</P>
			<P className="mt-4">This means the right one to use depends on the situation. Neither is for purely decorative purposes, that's what CSS styling is for.</P>
			<P className="mt-4">
				An example for <Code>{'<em>'}</Code> could be: "Just <em>do</em> it already!", or: "We <em>had</em> to do something about it". A person or software reading the text would
				pronounce the words in italics with an emphasis, using verbal stress.
			</P>
			<P className="mt-4">
				An example for <Code>{'<i>'}</Code> could be: "The <i>Queen Mary</i> sailed last night". Here, there is no added emphasis or importance on the word "Queen Mary". It is merely
				indicated that the object in question is not a queen named Mary, but a ship named Queen Mary. Another example for <Code>{'<i>'}</Code> could be: "The word <i>the</i> is an
				article".
			</P>
			<Features features={features} />
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default SpanRoute
